<!--
StaffCreditsSyncModal — franchise picker for the credit-purchase reconciliation
door (#27 follow-up, "macanetas" round): POST ai/credits/admin/sync/{franquia_id}.

Scoped to a FRANCHISE, not a subscription — StaffCreditsPage has no selected-
franchise context of its own (it's a global admin console, not a per-franchise
page), so this modal owns the <franchise-select> the same way GraceGrantModal
and StaffCreditsTopupModal/Form do. On success it opens the SAME
ReconciliationResultModal the subscription-sync button opens (shared by lane).

Gate is the ROUTE itself (Full-only, mirrored by StaffCreditsPage's isFull
check on the opening button) — never `can_reconcile`, which belongs to a
subscription's provider and does not apply to this door.
-->
<template>
  <modal
    :show="isVisible"
    :title="t('Sincronizar compras de créditos')"
    elem-id="modal_staff_credits_sync"
    size="compactDesktop"
    :close-label="t('Cancelar')"
    :save-label="t('Sincronizar')"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <staff-credits-sync-form
        ref="formRef"
        data-testid="staff-credits-sync-form"
        @submit-success="handleSubmitSuccess"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import permissionsService from '../../../services/permissionsService';
import StaffCreditsSyncForm, { type StaffCreditsSyncResult } from './StaffCreditsSyncForm.vue';

const { t } = useI18n();

const isVisible = ref(false);
const formRef = ref<InstanceType<typeof StaffCreditsSyncForm> | null>(null);

function handleOpen(): void {
  log('StaffCreditsSyncModal', 'handleOpen', 'Opening credit-purchase sync modal');
  isVisible.value = true;
}

function handleClose(): void {
  log('StaffCreditsSyncModal', 'handleClose', 'Closing credit-purchase sync modal');
  isVisible.value = false;
}

async function handleSave(): Promise<void> {
  log('StaffCreditsSyncModal', 'handleSave', 'Triggering form submit');
  await formRef.value?.handleSubmit();
}

function handleSubmitSuccess(result: StaffCreditsSyncResult): void {
  log('StaffCreditsSyncModal', 'handleSubmitSuccess', 'Sync complete, opening result', {
    franquiaId: result.franquiaId,
    rowCount: result.rows.length,
  });
  eventBus.emit('staffCreditsSync.saved');
  eventBus.emit('globalMessage.success', t('Compras de créditos sincronizadas'));
  handleClose();
  // The staff member picked the franchise BY NAME in <franchise-select>, so the
  // result is labelled by name too. Echoing the internal id back at them names
  // nothing they recognize. Falls back to the id only when the name is genuinely
  // unavailable — an empty label would be worse than a raw id.
  const franchiseName = permissionsService.getFranchiseName(result.franquiaId);
  const franchiseLabel = franchiseName !== '' ? franchiseName : `#${result.franquiaId}`;
  // No `truncated` on this payload — the credits lane never has one (see
  // this file's own docblock and StaffCreditsSyncForm's result type).
  eventBus.emit('modal.reconciliation-result.open', {
    title: `${t('Compras de créditos')} — ${franchiseLabel}`,
    rows: result.rows,
  });
}

onMounted(() => {
  log('StaffCreditsSyncModal', 'mounted', 'Registering modal listener');
  eventBus.on('modal.staff-credits-sync.open', handleOpen);
});

onBeforeUnmount(() => {
  log('StaffCreditsSyncModal', 'beforeUnmount', 'Removing modal listener');
  eventBus.off('modal.staff-credits-sync.open', handleOpen);
});
</script>
